import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText, ShoppingBag, Building2, ArrowRight, Shield } from 'lucide-react';
import { useShellState } from '@/app/context/ShellContext';
import { usePermission } from '@/hooks/usePermission';
import { NAVIGATION_ITEMS } from '@/config/navigation.config';
import { MOCK_PURCHASE_REQUESTS, MOCK_PURCHASE_ORDERS, MOCK_VENDORS } from '@/mocks/database';
import { ROUTES } from '@/constants/routes';

export const CommandPalette: React.FC = () => {
  const { isCommandPaletteOpen, setIsCommandPaletteOpen } = useShellState();
  const { can } = usePermission();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCommandPaletteOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isCommandPaletteOpen]);

  if (!isCommandPaletteOpen) return null;

  // Navigation Items filtered by permission
  const navCommands = NAVIGATION_ITEMS.filter(
    (item) =>
      (!item.requiredPermissions || item.requiredPermissions.every((p) => can(p))) &&
      item.label.toLowerCase().includes(query.toLowerCase())
  ).map((item) => ({
    id: item.id,
    title: item.label,
    subtitle: `Navigate • ${item.group}`,
    icon: item.icon,
    action: () => {
      navigate(item.path);
      setIsCommandPaletteOpen(false);
    },
  }));

  // Entity Quick Results
  const entityCommands = [
    ...MOCK_PURCHASE_REQUESTS.filter((r) => r.requestNumber.toLowerCase().includes(query.toLowerCase()) || r.title.toLowerCase().includes(query.toLowerCase())).map((r) => ({
      id: `cmd-req-${r.id}`,
      title: `${r.requestNumber} - ${r.title}`,
      subtitle: `Purchase Requisition • ${r.status}`,
      icon: FileText,
      action: () => {
        navigate(ROUTES.REQUESTS.DETAILS(r.id));
        setIsCommandPaletteOpen(false);
      },
    })),
    ...MOCK_PURCHASE_ORDERS.filter((p) => p.poNumber.toLowerCase().includes(query.toLowerCase())).map((p) => ({
      id: `cmd-po-${p.id}`,
      title: `${p.poNumber} - ${p.vendorName}`,
      subtitle: `Purchase Order • ${p.status}`,
      icon: ShoppingBag,
      action: () => {
        navigate(ROUTES.PURCHASE_ORDERS.DETAILS(p.id));
        setIsCommandPaletteOpen(false);
      },
    })),
    ...MOCK_VENDORS.filter((v) => v.name.toLowerCase().includes(query.toLowerCase())).map((v) => ({
      id: `cmd-vend-${v.id}`,
      title: `${v.vendorCode} - ${v.name}`,
      subtitle: `Vendor Directory • ${v.category}`,
      icon: Building2,
      action: () => {
        navigate(ROUTES.VENDORS.DETAILS(v.id));
        setIsCommandPaletteOpen(false);
      },
    })),
  ];

  const allItems = [...navCommands, ...entityCommands];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, allItems.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + allItems.length) % Math.max(1, allItems.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = allItems[selectedIndex];
      if (selected) selected.action();
    } else if (e.key === 'Escape') {
      setIsCommandPaletteOpen(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 pt-20 backdrop-blur-xs animate-in fade-in">
      <div
        className="w-full max-w-2xl rounded-xl border border-border bg-card shadow-2xl overflow-hidden flex flex-col"
        onKeyDown={handleKeyDown}
      >
        {/* Search Input Bar */}
        <div className="flex items-center border-b border-border px-4 py-3.5 bg-surface">
          <Search className="h-4 w-4 text-primary mr-3" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Type a command or search entities (Requests, POs, Vendors)..."
            className="w-full bg-transparent text-sm font-medium placeholder:text-foreground-muted focus:outline-none"
          />
          <button onClick={() => setIsCommandPaletteOpen(false)} className="rounded p-1 text-foreground-muted hover:bg-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Command Items List */}
        <div className="max-h-96 overflow-y-auto p-2 space-y-1 custom-scrollbar">
          {allItems.length === 0 ? (
            <div className="p-8 text-center text-xs text-foreground-muted">
              No matching commands or entities found for "{query}".
            </div>
          ) : (
            allItems.map((item, idx) => {
              const Icon = item.icon;
              const isSelected = idx === selectedIndex;

              return (
                <div
                  key={item.id}
                  onClick={item.action}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`flex items-center justify-between rounded-md p-2.5 text-xs font-semibold cursor-pointer transition-colors ${
                    isSelected ? 'bg-primary text-white' : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`rounded p-1.5 ${isSelected ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-bold">{item.title}</p>
                      <p className={`text-[10px] ${isSelected ? 'text-white/80' : 'text-foreground-secondary'}`}>
                        {item.subtitle}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className={`h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-40'}`} />
                </div>
              );
            })
          )}
        </div>

        {/* Footer Shortcut Bar */}
        <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-2 text-[10px] text-foreground-muted">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="rounded border bg-background px-1 py-0.5 font-mono">↑↓</kbd> Navigate
            </span>
            <span>
              <kbd className="rounded border bg-background px-1 py-0.5 font-mono">↵</kbd> Select
            </span>
            <span>
              <kbd className="rounded border bg-background px-1 py-0.5 font-mono">Esc</kbd> Close
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Shield className="h-3 w-3 text-primary" /> Permission Filtered
          </span>
        </div>
      </div>
    </div>
  );
};
